import axios from "axios";
import moment from "moment";
import md5 from "md5";

/**
 * Validate email and password before sending requests
 */
export const validateEmail = (email: string): boolean => /\S+@\S+\.\S+/.test(email);
export const validatePassword = (password: string): boolean => password.length >= 8;
export const validateBirthdayDate = (value: string) => {
  if (!value) return;

  const [day, month, year] = value.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  const now = new Date();

  // Validate real date
  const isValid = date.getDate() === day
    && date.getMonth() === month - 1
    && date.getFullYear() === year;

  if (!isValid) {
    throw new Error('Ngày sinh không hợp lệ')
  }

  // Validate age
  const age = now.getFullYear()
    - year
    - (now.getMonth() < month - 1
      || (now.getMonth() === month - 1 && now.getDate() < day)
      ? 1
      : 0);

  if (age < 18) {
    throw new Error('Bạn chưa đủ 18 tuổi để tham gia trò chơi')
  }

  if (age > 100) {
    throw new Error('Tuổi phải nằm trong khoảng 18 đến 100')
  }

  return Promise.resolve();
};
/** SĐT di động VN 10 số + đầu số nhà mạng hợp lệ. */
export const validatePhoneNumber = (phone: string) => /^0(3[2-9]|5[2689]|7[06-9]|8[1-9]|9\d)\d{7}$/.test(phone);
export const validateCode = (code: string) => code.length >= 4;

export async function getMe(accessToken: string, apiEndpoint: string) {
  const response = await axios.get(`${apiEndpoint}/users/me`, {
    headers: { Authorization: accessToken }
  });
  return response.data.data;
}

/** Ghi DAU (type=ping) — tối đa 2 lần/ngày/product (khớp cap server); lỗi không chặn flow. */
const H5_PING_CAP_STORAGE_KEY = 'joygamesH5PingCap';
const H5_PING_MAX_PER_DAY = 2;

function readH5PingCap(): { date: string; counts: Record<string, number> } {
  try {
    const raw = localStorage.getItem(H5_PING_CAP_STORAGE_KEY);
    if (!raw) return { date: '', counts: {} };
    const parsed = JSON.parse(raw) as { date?: string; counts?: Record<string, number> };
    return {
      date: typeof parsed?.date === 'string' ? parsed.date : '',
      counts: parsed?.counts && typeof parsed.counts === 'object' ? parsed.counts : {},
    };
  } catch {
    return { date: '', counts: {} };
  }
}

function writeH5PingCap(date: string, counts: Record<string, number>) {
  try {
    localStorage.setItem(H5_PING_CAP_STORAGE_KEY, JSON.stringify({ date, counts }));
  } catch {
    // ignore quota / private mode
  }
}

export async function pingActivity(
  accessToken: string,
  apiEndpoint: string,
  productId?: string | number
) {
  const dateKey = moment().format('YYYY-MM-DD');
  const pid = productId != null && String(productId) !== '' ? String(productId) : '_';
  let { date, counts } = readH5PingCap();
  if (date !== dateKey) {
    date = dateKey;
    counts = {};
  }
  if ((counts[pid] || 0) >= H5_PING_MAX_PER_DAY) {
    return;
  }

  try {
    await axios.get(`${apiEndpoint}/auth/ping`, {
      headers: {
        Authorization: accessToken,
        ...(pid !== '_' ? { 'X-Product-Id': pid } : {}),
      },
    });
    counts[pid] = (counts[pid] || 0) + 1;
    writeH5PingCap(dateKey, counts);
  } catch {
    // ignore
  }
}

/**
 * Login with username/password
 */
export async function loginWithUsername({ username, password, productId }: any, apiEndpoint: string) {
  if (!validateEmail(username)) throw new Error("Định dạng email chưa đúng");
  if (!validatePassword(password)) throw new Error("Mật khẩu phải chứa ít nhất 8 ký tự");

  const response = await axios.post(`${apiEndpoint}/auth/login`, {
    username,
    password: md5(password),
    isWebsite: true,
    ...(productId ? { productId } : {}),
  });
  const user = await getMe(response.data.data.accessToken, apiEndpoint)
  return { ...response.data.data, user };
}

export async function guestLogin(apiEndpoint: string, productId?: string) {
  const response = await axios.post(`${apiEndpoint}/auth/login/guest`, {
    isWebsite: true,
    platform: 'h5',
    ...(productId ? { productId } : {}),
  });
  const data = response.data.data || {};
  const user = await getMe(data.accessToken, apiEndpoint)
  return { ...data, user };
}

/**
 * Social login with Google, Facebook, Apple, Zalo
 */
export async function loginWithProvider(provider: "google" | "facebook" | "apple" | "zalo", payload: any, apiEndpoint: string, productId?: string) {
  payload.code = payload.access_token || payload.accessToken || payload.state || payload.authorization?.id_token;
  payload.type = 'h5';
  payload.platform = 'h5';
  if (productId) payload.productId = productId;
  const response = await axios.post(`${apiEndpoint}/auth/login/${provider}`, { ...payload, isWebsite: provider === 'apple' ? false : true });
  const data = response.data.data || {};
  const user = await getMe(data.accessToken, apiEndpoint);
  return { ...data, user };
}

export async function signup({ email, password, productId }: any, apiEndpoint: string) {
  if (!validateEmail(email)) throw new Error('Định dạng email chưa đúng');
  if (!validatePassword(password)) throw new Error('Mật khẩu phải chứa ít nhất 8 ký tự');

  const response = await axios.post(`${apiEndpoint}/auth/register`, {
    email,
    username: email,
    password: md5(password),
    platform: 'h5',
    ...(productId ? { productId } : {}),
  });
  const data = response.data.data || {};
  const user = await getMe(data.accessToken, apiEndpoint)
  return { ...data, user, isNewUser: true };
}

export async function sendPhoneCode({ phoneNumber }: any, apiEndpoint: string, accessToken: string) {
  if (!validatePhoneNumber(phoneNumber)) throw new Error('Số điện thoại không hợp lệ (phải là SĐT di động Việt Nam 10 số)');
  const response = await axios.post(
    `${apiEndpoint}/auth/phone/validate`,
    { phoneNumber },
    {
      headers: {
        Authorization: accessToken
      }
    }
  );
  return response.data;
}

export async function verifyPhoneCode({ phoneNumber, code }: any, apiEndpoint: string, accessToken: string) {
  if (!validatePhoneNumber(phoneNumber)) throw new Error('Số điện thoại không hợp lệ (phải là SĐT di động Việt Nam 10 số)');
  if (!validateCode(code)) throw new Error('Hãy nhập mã code');

  const response = await axios.put(
    `${apiEndpoint}/auth/phone/validate`,
    { phoneNumber, code },
    {
      headers: {
        Authorization: accessToken
      }
    }
  );
  return response.data.data;
}

export async function updateMe(
  { name, birthdayDate }: { name: string; birthdayDate: string },
  apiEndpoint: string,
  accessToken: string
) {
  const trimmedName = (name || '').trim();
  if (!trimmedName) throw new Error('Hãy nhập họ và tên');
  if (trimmedName.length < 3) throw new Error('Họ tên phải chứa ít nhất 3 ký tự');
  if (!/^[A-Za-zÀ-ỹ\s]+$/u.test(trimmedName)) {
    throw new Error('Họ tên không thể chứa số hoặc ký tự đặc biệt');
  }
  if (!birthdayDate) throw new Error('Hãy nhập ngày tháng năm sinh');
  await validateBirthdayDate(birthdayDate);

  const response = await axios.put(
    `${apiEndpoint}/users/me`,
    {
      name: trimmedName,
      birthdayDate: moment(birthdayDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
    },
    {
      headers: {
        Authorization: accessToken
      }
    }
  );
  return response.data.data;
}

export async function sendForgotCode({ email }: any, apiEndpoint: string) {
  if (!validateEmail(email)) throw new Error('Định dạng email chưa đúng');
  const response = await axios.post(
    `${apiEndpoint}/auth/forgot`,
    { email }
  );
  return response.data.data;
}

export async function verifyForgotCode({ email, code }: any, apiEndpoint: string) {
  if (!validateEmail(email)) throw new Error('Định dạng email chưa đúng');
  if (code && !validateCode(code)) throw new Error('Hãy nhập mã code');
  const response = await axios.post(
    `${apiEndpoint}/auth/forgot/verify`,
    { email, code }
  );
  return response.data.data;
}

export async function changeForgotPassword({ email, code, password }: any, apiEndpoint: string) {
  if (!validateEmail(email)) throw new Error('Định dạng email chưa đúng');
  if (!validatePassword(password)) throw new Error('Mật khẩu phải chứa ít nhất 8 ký tự');
  if (code && !validateCode(code)) throw new Error('Hãy nhập mã code');
  const response = await axios.put(
    `${apiEndpoint}/auth/forgot`,
    { email, code, password: md5(password) }
  );
  return response.data.data;
}